import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ProposalPortalSession {
  shareCode: string;
  proposalId: string;
  brandId: string;
  clientName: string;
  isValid: boolean;
}

interface ProposalPortalContextType {
  session: ProposalPortalSession | null;
  isLoading: boolean;
  validateAccess: (shareCode: string, clientEmail: string, accessPin: string) => Promise<boolean>;
  logout: () => void;
}

const ProposalPortalContext = createContext<ProposalPortalContextType | undefined>(undefined);

const STORAGE_KEY = 'proposal-portal-session';

export const ProposalPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<ProposalPortalSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const storedSession = localStorage.getItem(STORAGE_KEY);
    if (storedSession) {
      try {
        const parsedSession = JSON.parse(storedSession);
        setSession(parsedSession);
      } catch (error) {
        console.error('Error parsing stored session:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const validateAccess = async (
    shareCode: string,
    clientEmail: string,
    accessPin: string
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_proposal_access', {
        p_share_code: shareCode,
        p_client_email: clientEmail,
        p_access_pin: accessPin
      });

      if (error) throw error;

      const result = data as { valid?: boolean; proposal_id?: string; brand_id?: string; client_name?: string } | null;

      if (result?.valid) {
        const newSession: ProposalPortalSession = {
          shareCode,
          proposalId: result.proposal_id!,
          brandId: result.brand_id!,
          clientName: result.client_name!,
          isValid: true
        };
        
        setSession(newSession);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error validating access:', error);
      return false;
    }
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <ProposalPortalContext.Provider value={{ session, isLoading, validateAccess, logout }}>
      {children}
    </ProposalPortalContext.Provider>
  );
};

export const useProposalPortal = () => {
  const context = useContext(ProposalPortalContext);
  if (context === undefined) {
    throw new Error('useProposalPortal must be used within a ProposalPortalProvider');
  }
  return context;
};
